import { type VariantProps } from 'class-variance-authority';
import { isValidElement, Ref, useMemo } from 'react';

import { Icon, type IconType } from '@/app/components/icons';
import { IconExpandMore } from '@/app/components/icons/ui-actions/icon-expand-more';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const borderClassName =
  'before:transition-form before:pointer-events-none before:absolute before:inset-0 before:border-b';

const AccordionStyled = cnva(
  [
    'text-sub-h3 font-noto-bold transition-form relative flex w-full cursor-pointer gap-x-2 p-4 text-left select-none',
    'focus-visible:outline-none',
    borderClassName,
  ],
  {
    variants: {
      theme: {
        light:
          'bg-white before:border-gray-200 hover:bg-gray-50 focus-visible:bg-gray-50',
        dark: 'bg-gray-900 text-white before:border-gray-500 hover:bg-gray-700 focus-visible:bg-gray-700',
      },
      isOpen: {
        true: 'text-red-500',
        false: '',
      },
    },
    defaultVariants: {
      theme: 'light',
      isOpen: false,
    },
  }
);

const AccordionContentStyled = cnva(['relative p-4', borderClassName], {
  variants: {
    theme: {
      light: 'bg-white text-gray-700 before:border-b-gray-200',
      dark: 'bg-gray-900 text-gray-50 before:border-b-gray-500',
    },
    isOpen: {
      true: '',
      false: '',
    },
    withDescription: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      isOpen: false,
      withDescription: true,
      className: '[&>*]:line-clamp-4 [&>*]:h-24',
    },
    {
      isOpen: true,
      withDescription: true,
      className: '[&>*]:space-y-4',
    },
  ],
  defaultVariants: {
    theme: 'light',
    isOpen: false,
    withDescription: false,
  },
});

export interface AccordionProps {
  /**
   * (Optional) The ref for the accordion
   */
  ref?: Ref<HTMLDivElement>;

  /**
   * (Optional) The class name for the accordion
   */
  className?: string;

  /**
   * (Optional) The class name for the content of the accordion
   */
  contentClassName?: string;

  /**
   * (Optional) The theme of the accordion
   */
  theme?: VariantProps<typeof AccordionStyled>['theme'];

  /**
   * (Optional) The title of the accordion
   */
  title?: string | React.ReactNode;

  /**
   * (Optional) The icon to display
   */
  leftIcon?: React.ReactNode;

  /**
   * The content (can be html) to display inside the accordion
   */
  content: React.ReactNode;

  /**
   * (Optional) Whether to show the description
   */
  withDescription?: boolean;

  /**
   * (Optional) The callback function to handle the change event
   */
  onChange?: (id: string, isOpen: boolean) => void;

  /**
   * The value of the accordion
   */
  value: string;

  /**
   * (Optional) Whether the accordion is open
   */
  isOpen?: boolean;
}

export const Accordion = ({
  ref,
  className,
  contentClassName,
  theme,
  title,
  content,
  leftIcon = null,
  withDescription = false,
  onChange,
  value,
  isOpen = false,
  ...props
}: AccordionProps) => {
  const handleChange = (value: string) => {
    safeCall(onChange, value, !isOpen);
  };

  const isIconType = useMemo(() => {
    if (!isValidElement(leftIcon)) return;
    const functionName = (
      leftIcon.type?.valueOf() as unknown as { name: string }
    )?.name;
    return (
      typeof leftIcon.type === 'function' && functionName === 'IconSettings'
    );
  }, [leftIcon]);

  return (
    <div
      ref={ref}
      {...createDataAttributes({
        test: 'accordion',
      })}
      {...props}
    >
      <button
        type="button"
        id={`${value}-title`}
        className={AccordionStyled({
          theme,
          className,
          isOpen,
        })}
        onClick={() => handleChange(value)}
        aria-expanded={isOpen}
        aria-controls={`${value}-content`}
        {...createDataAttributes({
          test: 'accordion-title',
        })}
        {...props}
      >
        {!!leftIcon &&
          (isIconType ? (
            <Icon className="pointer-events-none" icon={leftIcon as IconType} />
          ) : (
            leftIcon
          ))}
        {!!title && title}
        <Icon
          icon={<IconExpandMore />}
          className={cn(
            'pointer-events-none ml-auto transition-transform duration-300',
            {
              'rotate-180': isOpen,
            }
          )}
        />
      </button>
      {!!(isOpen || withDescription) && (
        <div
          id={`${value}-content`}
          role="region"
          aria-labelledby={`${value}-title`}
          className={AccordionContentStyled({
            theme,
            isOpen,
            withDescription,
            className: contentClassName,
          })}
          {...createDataAttributes({
            test: 'accordion-content',
          })}
        >
          {content}
        </div>
      )}
    </div>
  );
};
